import { Merediem, TimeAMPM } from '../enums';
import { DecimalRegex } from '../constants';
import { InputErrorModel } from '../models/InputErrorModel';

export class CoreInputModelUtil {
  public static capitalize(value: string): string {
    if (!value) {
      return '';
    }

    return value
      .toLowerCase()
      .replace(/\b\w/g, (letter: string) => letter.toUpperCase())
      .trim();
  }

  public static removeWhiteSpace(value: string): string {
    if (!value) {
      return '';
    }

    return value.trim();
  }

  public static isValidString(value: string): boolean {
    if (!value) return false;
    return value?.toString().trim().length > 0;
  }

  public static isValidNumber(value: number): boolean {
    return value > 0;
  }

  public static isValidNumberString(value: string): boolean {
    return !/^\d*\.?\d*$/.test(value);
  }

  public static validateTextField<T>(
    value: string,
    field: keyof T,
    errors: InputErrorModel<T>[],
    index?: number,
    message?: string
  ) {
    if (!CoreInputModelUtil.isValidString(value)) {
      errors.push(new InputErrorModel<T>({ field: field, index: index, message: message }));
    }
  }

  static validateDecimalValue<T>(
    value: string,
    field: keyof T,
    errors: InputErrorModel<T>[],
    index?: number,
    isValidateGreaterThanZero?: boolean,
    message?: string
  ): boolean {
    if (!this.isValidString(value)) {
      errors.push(new InputErrorModel<T>({ field: field, index: index, message: message }));
      return false;
    }

    if (!DecimalRegex.test(value)) {
      errors.push(new InputErrorModel<T>({ field: field, index: index, message: message }));
      return false;
    } else if (isValidateGreaterThanZero && parseFloat(value) <= 0) {
      errors.push(new InputErrorModel<T>({ field: field, index: index, message: message }));
      return false;
    }

    return true;
  }

  static validatePercentage<T>(
    value: string,
    field: keyof T,
    errors: InputErrorModel<T>[],
    index?: number,
    isValidateGreaterThanZero?: boolean,
    message?: string
  ): boolean {
    if (!this.isValidString(value)) {
      errors.push(new InputErrorModel<T>({ field: field, index: index, message: message }));
      return false;
    }

    if (!DecimalRegex.test(value)) {
      errors.push(new InputErrorModel<T>({ field: field, index: index, message: message }));
      return false;
    } else if (isValidateGreaterThanZero && (parseFloat(value) <= 0 || parseFloat(value) > 100)) {
      errors.push(new InputErrorModel<T>({ field: field, index: index, message: message }));
      return false;
    }

    return true;
  }

  static validateDigit(input: string) {
    const digitPattern = /^\d+$/;
    return digitPattern.test(input);
  }
  static validateNumber<T>(value: number, field: keyof T, errors: InputErrorModel<T>[], message?: string) {
    if (!this.isValidNumber(value)) {
      errors.push(new InputErrorModel<T>({ field: field, message: message }));
    }
  }

  static removeSpecialCharactersWithoutSpace(value: string): string {
    if (!value) {
      return '';
    }
    return value.replace(/[^a-zA-Z0-9 ]/g, '');
  }

  static addError<T>(field: keyof T, errors: InputErrorModel<T>[], message?: string, index?: number) {
    errors.push(new InputErrorModel<T>({ field: field, message: message, index: index }));
  }

  public static validateTaxId<T>(field: keyof T, value: any, errors: InputErrorModel<T>[]): void {
    const einValue = value as string;

    if (!/^\d{2}-?\d{7}$/.test(einValue)) {
      errors.push(
        new InputErrorModel<T>({
          field: field,
          message: 'Tax ID must be in the format XX-XXXXXXX or XXXXXXXXX, where X is a digit.',
        })
      );
    }
  }

  public static isValidTaxId(value: string): boolean {
    if (!value) return false;
    return /^\d{2}-?\d{7}$/.test(value);
  }

  public static validateWebsiteUrl(webUrl: string): boolean {
    const WEBSITE_URL_REGEX = new RegExp(
      '^(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$',
      'i'
    );
    return WEBSITE_URL_REGEX.test(webUrl);
  }

  public static validateWebsite<T>(field: keyof T, value: any, errors: InputErrorModel<T>[]): void {
    const websiteValue = value as string;

    if (!this.validateWebsiteUrl(websiteValue)) {
      errors.push(
        new InputErrorModel<T>({
          field: field,
          message: 'Please enter a valid Website URL',
        })
      );
    }
  }

  public static isValidPhoneNumber = (phoneNumber: string): boolean => {
    return /^\d{10}$/.test(phoneNumber);
  };

  public static validatePhoneNumber<T>(field: keyof T, value: any, errors: InputErrorModel<T>[]): void {
    const phoneNumberValue = value as string;
    if (!this.isValidPhoneNumber(phoneNumberValue)) {
      errors.push(
        new InputErrorModel<T>({
          field: field,
          message: 'Phone number must be 10 digits long.',
        })
      );
    }
  }

  public static validateDBA<T>(field: keyof T, value: any, errors: InputErrorModel<T>[]): void {
    const dbaValue = value as string;
    if (!dbaValue) return;
    if (!(dbaValue?.length >= 2 && dbaValue?.length <= 100)) {
      errors.push(
        new InputErrorModel<T>({
          field: field,
          message: 'DBA Name length should be from 2 to 100 characters.',
        })
      );
    }
  }

  public static validateEmail<T>(field: keyof T, email: any, errors: InputErrorModel<T>[]): void {
    const isValid = /^([a-zA-Z0-9_.+-])+\@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/.test(email);
    if (!isValid) {
      errors.push(
        new InputErrorModel<T>({
          field: field,
          message: 'Please enter valid email address',
        })
      );
    }
  }

  public static isValidEmail = (email: string): boolean => {
    return /^([a-zA-Z0-9_.+-])+\@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/.test(email);
  };

  public static validateDateField<T>(
    field: keyof T,
    value: string,
    errors: InputErrorModel<T>[],
    message?: string
  ): boolean {
    const date = new Date(value);
    const isValid = date.toString() !== 'Invalid Date';
    if (!isValid) {
      errors.push(
        new InputErrorModel<T>({
          field: field,
          message: message || 'Please enter valid date',
        })
      );
    }
    return isValid;
  }

  static isValidJSON = (text: string): boolean => {
    try {
      JSON.parse(text);
      return true;
    } catch {
      return false;
    }
  };

  static validateTime<T>(
    time: string,
    field: keyof T,
    amPm: keyof typeof TimeAMPM,
    errors: InputErrorModel<T>[],
    index?: number
  ): boolean {
    if (!this.validateTimeAndAmPm(time, amPm)) {
      errors.push(new InputErrorModel<T>({ field: field, index: index }));
      return false;
    }

    return true;
  }

  static validateTimeAndAmPm(time: string, amPm: keyof typeof TimeAMPM): boolean {
    // Regular expression to match hh:mm, h:mm, hh:m, or h:m format
    const timeRegex = /^([1-9]|0[1-9]|1[0-2]):([0-5]?\d)$/;
    // Check if time matches the hh:mm format
    const isTimeValid = timeRegex.test(time);

    // Check if amPm is either "AM" or "PM"
    const isAmPmValid = amPm === 'AM' || amPm === 'PM';

    return isTimeValid && isAmPmValid;
  }

  public static validateDateAndTime<T>(
    fieldDate: keyof T,
    fieldTime: keyof T,
    dateValue: Date,
    timeValue: string,
    meridiem: keyof typeof Merediem,
    errors: InputErrorModel<T>[],
    minutesGap: number = 2
  ): boolean {
    const currentDate = new Date();
    currentDate.setHours(currentDate.getHours(), currentDate.getMinutes(), 0, 0);
    const todayStart = new Date(currentDate);
    todayStart.setHours(0, 0, 0, 0);
    const dateStart = new Date(dateValue);
    dateStart.setHours(0, 0, 0, 0);
    if (dateStart.getTime() < todayStart.getTime()) {
      errors.push({
        field: fieldDate,
        message: 'Please select a future date.',
      });
      return false;
    } else if (dateStart.getTime() === todayStart.getTime()) {
      const selectedTime = new Date(`${dateValue.toDateString()} ${timeValue} ${meridiem}`);
      const currentTimePlusFive = new Date(currentDate.getTime() + minutesGap * 60000);

      if (selectedTime.getTime() < currentTimePlusFive.getTime()) {
        errors.push({
          field: fieldTime,
          message: `Please select a future time (at least ${minutesGap} minutes from now).`,
        });

        return false;
      }
    }
    return true;
  }

  public static isFutureDateTime(
    dateValue: Date,
    timeValue: string,
    meridiem: keyof typeof Merediem,
    minutesGap: number = 2
  ): boolean {
    const currentDate = new Date();
    currentDate.setSeconds(0, 0); // Reset seconds and milliseconds

    const selectedDate = new Date(dateValue);
    selectedDate.setHours(0, 0, 0, 0);

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    // If the selected date is in the past
    if (selectedDate < todayStart) {
      return false;
    }

    // Parse timeValue (HH:mm) and convert it to 24-hour format
    let [hours, minutes] = timeValue.split(':').map(Number);
    if (meridiem === 'PM' && hours < 12) {
      hours += 12;
    } else if (meridiem === 'AM' && hours === 12) {
      hours = 0;
    }

    // Set extracted time into the selected date object
    selectedDate.setHours(hours, minutes, 0, 0);

    // If it's today, ensure the selected time is at least `minutesGap` minutes ahead
    const currentTimePlusGap = new Date(currentDate.getTime() + minutesGap * 60000);
    return selectedDate.getTime() > currentTimePlusGap.getTime();
  }
}
